import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { num } from "@/lib/format";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common/States";
import { DoorOpen } from "lucide-react";
import { ROOM_DOT, ROOM_TILE, Legend, dayLabel, roomStatusLabel } from "./AccommodationBits";

const DENSITY = { kompakt: 22, normal: 28, komfort: 34 };

/**
 * High-density operational room grid – Bygg → Etasje → Modul → Rom (design_guidelines → room_map_grammar).
 * role=grid with arrow-key navigation; a tile shows only its status dot, the code appears in the tooltip and on hover.
 */
export const RoomMap = ({ data, selectedId, onSelect, className }) => {
  const [density, setDensity] = useState(() => localStorage.getItem("room-map-density") || "normal");
  const [building, setBuilding] = useState("all");
  const [floor, setFloor] = useState("all");
  const [status, setStatus] = useState(null);
  const gridRef = useRef(null);

  useEffect(() => localStorage.setItem("room-map-density", density), [density]);

  const rooms = useMemo(() => (data?.rooms || []).filter((r) => (building === "all" || String(r.building) === building) && (floor === "all" || String(r.floor) === floor) && (!status || r.status === status)), [data, building, floor, status]);
  const buildings = useMemo(() => [...new Set((data?.rooms || []).map((r) => String(r.building)))].sort(), [data]);
  const floors = useMemo(() => [...new Set((data?.rooms || []).filter((r) => building === "all" || String(r.building) === building).map((r) => String(r.floor)))].sort(), [data, building]);

  // Bygg → Etasje → Modul groups, preserving server order
  const groups = useMemo(() => {
    const out = [];
    for (const r of rooms) {
      let b = out.find((x) => x.building === String(r.building));
      if (!b) out.push((b = { building: String(r.building), floors: [] }));
      let f = b.floors.find((x) => x.floor === String(r.floor));
      if (!f) b.floors.push((f = { floor: String(r.floor), modules: [] }));
      let m = f.modules.find((x) => x.code === r.module_code);
      if (!m) f.modules.push((m = { code: r.module_code, rooms: [] }));
      m.rooms.push(r);
    }
    return out;
  }, [rooms]);

  const flat = useMemo(() => groups.flatMap((b) => b.floors.flatMap((f) => f.modules.flatMap((m) => m.rooms))), [groups]);

  const onKeyDown = useCallback(
    (e, idx) => {
      const cols = Math.max(1, Math.floor((gridRef.current?.clientWidth || 600) / (DENSITY[density] + 6)));
      const moves = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols };
      if (moves[e.key] !== undefined) {
        e.preventDefault();
        const next = flat[Math.max(0, Math.min(flat.length - 1, idx + moves[e.key]))];
        if (next) gridRef.current?.querySelector(`[data-room-id="${next.room_id}"]`)?.focus();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect?.(flat[idx]);
      } else if (e.key === "Escape") {
        onSelect?.(null);
      }
    },
    [flat, density, onSelect]
  );

  const size = DENSITY[density];
  let idx = -1;

  return (
    <div className={cn("space-y-4", className)} style={{ "--room-tile": `${size}px` }} data-testid="room-map">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Select value={building} onValueChange={(v) => { setBuilding(v); setFloor("all"); }}>
          <SelectTrigger className="h-8 w-[120px] text-[12.5px]" data-testid="room-map-building-select"><SelectValue placeholder="Bygg" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle bygg</SelectItem>{buildings.map((b) => <SelectItem key={b} value={b}>Bygg {b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={floor} onValueChange={setFloor}>
          <SelectTrigger className="h-8 w-[130px] text-[12.5px]" data-testid="room-map-floor-select"><SelectValue placeholder="Etasje" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle etasjer</SelectItem>{floors.map((f) => <SelectItem key={f} value={f}>Etasje {f}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-[12px] text-muted-foreground sm:inline">{num(rooms.length)} rom</span>
          <ToggleGroup type="single" value={density} onValueChange={(v) => v && setDensity(v)} className="rounded-md border bg-background p-0.5" data-testid="room-map-density-toggle">
            {Object.keys(DENSITY).map((k) => (
              <ToggleGroupItem key={k} value={k} className="h-7 px-2 text-[11.5px] capitalize data-[state=on]:bg-muted" data-testid={`room-map-density-${k}`}>{k}</ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      <Legend counts={data?.counts} active={status} onPick={(k) => setStatus((s) => (s === k ? null : k))} />

      {!rooms.length ? (
        <EmptyState icon={DoorOpen} title="Ingen rom i utvalget" description={status ? `Ingen rom med status «${roomStatusLabel(status)}».` : "Ingen rom er plassert på campen i dag."} testId="room-map-empty" className="py-10" />
      ) : (
        <TooltipProvider delayDuration={150}>
          <div ref={gridRef} role="grid" aria-label="Romkart" className="space-y-6" data-testid="room-map-grid">
            {groups.map((b) => (
              <section key={b.building} data-testid={`room-map-building-${b.building}`}>
                <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">Bygg {b.building} <span className="ml-1 font-mono text-[11px] font-normal">{num(b.floors.reduce((a, f) => a + f.modules.reduce((x, m) => x + m.rooms.length, 0), 0))} rom</span></h3>
                <div className="mt-2 space-y-3">
                  {b.floors.map((f) => (
                    <div key={f.floor} className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-4" role="row">
                      <div className="w-16 shrink-0 pt-1 text-[12px] text-muted-foreground">Etasje {f.floor}</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {f.modules.map((m) => (
                          <div key={m.code} className="flex items-end gap-1.5" data-testid={`room-map-module-${m.code}`}>
                            <div className="flex flex-wrap gap-1.5">
                              {m.rooms.map((r) => {
                                idx += 1;
                                const i = idx;
                                const selected = r.room_id === selectedId;
                                const occ = r.occupants?.length || 0;
                                return (
                                  <Tooltip key={r.room_id}>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        role="gridcell"
                                        data-room-id={r.room_id}
                                        aria-selected={selected}
                                        aria-label={`Rom ${r.operational_number} – ${roomStatusLabel(r.status)}`}
                                        tabIndex={selected || (i === 0 && !selectedId) ? 0 : -1}
                                        onKeyDown={(e) => onKeyDown(e, i)}
                                        onClick={() => onSelect?.(r)}
                                        className={cn(
                                          "relative flex items-center justify-center rounded-[10px] border text-[10px] font-mono leading-none text-transparent transition-[transform,box-shadow,color] duration-150 hover:-translate-y-px hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                          ROOM_TILE[r.status] || ROOM_TILE.ikke_aktiv,
                                          selected && "ring-2 ring-primary ring-offset-2 ring-offset-background text-foreground/80",
                                          r.turnaround && "after:absolute after:-bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-amber-500"
                                        )}
                                        style={{ width: "var(--room-tile)", height: "var(--room-tile)" }}
                                        data-testid={`room-map-room-tile-${r.room_id}`}
                                      >
                                        <span className={cn("absolute left-1 top-1 h-1.5 w-1.5 rounded-full", ROOM_DOT[r.status])} aria-hidden />
                                        {size >= 28 && <span>{r.beds > 1 ? `${occ}/${r.beds}` : ""}</span>}
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-[260px] text-[12px]">
                                      <div className="flex items-center gap-2 font-semibold"><span className={cn("h-2 w-2 rounded-full", ROOM_DOT[r.status])} /> {r.operational_number} <span className="font-mono text-[10.5px] font-normal text-muted-foreground">{r.physical_code}</span></div>
                                      <div className="text-muted-foreground">{roomStatusLabel(r.status)} · {r.beds} {r.beds === 1 ? "seng" : "senger"}{occ ? ` · ${occ} innsjekket` : ""}</div>
                                      {r.occupants?.slice(0, 3).map((o) => <div key={o.stay_id}>{o.name || o.employer_name}{o.planned_out ? ` · ut ${dayLabel(o.planned_out)}` : ""}</div>)}
                                      {r.upcoming?.[0] && <div className="text-muted-foreground">Neste: {r.upcoming[0].name || r.upcoming[0].employer_name} {dayLabel(r.upcoming[0].planned_in)}</div>}
                                      {r.notes && <div className="text-amber-700 dark:text-amber-300">{r.notes}</div>}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                            <span className="hidden pb-0.5 font-mono text-[10px] text-muted-foreground/70 sm:inline">{m.code.replace(/^SI-/, "")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
};
